import { createHash } from "node:crypto";
import { formatCents, readTypedDollars } from "./figures";
import { DAY_MS } from "./window";
// X integration 2: the ONE offer word list and reader live in ./offerWords (X3 fix round 1); X2 reads them.
// X integration 2b: through `holdsOfferWording`, the union X3's rail applied (the standing-offer reader,
// so "price match" and "no discounts" are held too, plus the word list and the link scan).
import { holdsOfferWording, urlWords } from "./offerWords";

/**
 * X2 CATALOG (Composer Spec 3 and 4 rail, Planning elements, CLAUDE.md
 * sections 4 and 8). The pure half of the catalog: what a product may be
 * inserted as, the one text an insert becomes, the CSV an owner uploads, and
 * which media may go in whose thread.
 *
 * PRECISION (section 4). A price here is integer cents from a record, never
 * typed into a draft and never written by a model. The text of an insert is
 * built by `composeProductInsert` from that record and nothing else, through
 * the one money module (`formatCents`, F-16), so the figure in a draft is the
 * figure in the catalog byte for byte, and the verifier can ground it to the
 * versioned `factRef`. A product that is sold, unavailable, of unknown
 * availability, past its freshness window or without a price is NOT insertable
 * and `composeProductInsert` refuses to build its text (fail loudly: a caller
 * that skipped `catalogInsertable` gets a throw, not a plausible line).
 *
 * NO OFFER (section 0.5). Nothing here reads, builds or forwards a discount,
 * sale or deal. A product carries one price and the insert states it.
 *
 * NO DEPENDENCY. The CSV reader is RFC 4180 by hand: a catalog file is the
 * owner's own data and the parser is small enough to pin.
 */

export const PRODUCT_SOURCES = ["feed", "pos", "csv", "manual"] as const;
export type ProductSource = (typeof PRODUCT_SOURCES)[number];

export const PRODUCT_AVAILABILITIES = ["in_stock", "low", "sold", "unavailable", "unknown"] as const;
export type ProductAvailability = (typeof PRODUCT_AVAILABILITIES)[number];

export const MEDIA_SOURCES = ["instagram", "facebook", "tiktok", "youtube", "gallery", "video", "customer_shared"] as const;
export type MediaSource = (typeof MEDIA_SOURCES)[number];

export const MEDIA_KINDS = ["image", "video", "reel", "story", "spin"] as const;
export type MediaKind = (typeof MEDIA_KINDS)[number];

export const MEDIA_RIGHTS = ["owned", "licensed", "customer_own", "permission_granted"] as const;
export type MediaRights = (typeof MEDIA_RIGHTS)[number];

const SECONDS_PER_DAY = Math.round(DAY_MS / 1000);

/**
 * How long a product record is trusted after `fetchedAt`, per source
 * (design open question 7, taken on its rec). AWAITING RATIFICATION: Kabir has
 * not ruled these four numbers. A feed is re-read daily, a POS reads stock that
 * moves within the hour, and a hand-typed or uploaded row is trusted for two
 * weeks and then the page asks the owner to confirm prices.
 */
export const PRODUCT_TTL_SECONDS: Readonly<Record<ProductSource, number>> = Object.freeze({
  feed: SECONDS_PER_DAY, // AWAITING RATIFICATION
  pos: 60 * 60, // AWAITING RATIFICATION
  csv: 14 * SECONDS_PER_DAY, // AWAITING RATIFICATION
  manual: 14 * SECONDS_PER_DAY, // AWAITING RATIFICATION
});

/** The freshness a media asset carries when its source does not say. AWAITING RATIFICATION (30 days). */
export const MEDIA_TTL_SECONDS = 30 * SECONDS_PER_DAY; // AWAITING RATIFICATION

/**
 * The lowest tag confidence the vision tagger's output is stored at (design,
 * "below threshold not stored"). AWAITING RATIFICATION: 0.8 is a placeholder
 * nobody calibrated. A tag below it is dropped, never stored as a weak tag.
 */
export const VISION_TAG_MIN_CONFIDENCE = 0.8; // AWAITING RATIFICATION

/** The tag kinds the vision tagger may produce. */
export const VISION_TAG_KINDS = ["metal", "stone", "cut", "setting", "style"] as const;
export type VisionTagKind = (typeof VISION_TAG_KINDS)[number];

export interface VisionTag {
  readonly kind: VisionTagKind;
  readonly value: string;
  readonly confidence: number;
}

/** The fields of a product row the pure functions read. */
export interface CatalogProduct {
  readonly id: string;
  readonly source: ProductSource;
  readonly sku: string | null;
  readonly name: string;
  readonly priceCents: number | null;
  readonly currency: string;
  readonly availability: ProductAvailability;
  readonly url: string | null;
  readonly fetchedAt: Date;
  readonly ttlSeconds: number;
  readonly version: number;
}

export type CatalogRefusal = "sold" | "unavailable" | "unknown_availability" | "stale" | "no_price" | "offer_wording";
export type CatalogInsertable = { readonly ok: true } | { readonly ok: false; readonly reason: CatalogRefusal };

/** The offer wording a product's customer-visible fields carry, or null. */
function productOfferWording(p: { readonly name?: string; readonly description?: string | null; readonly url?: string | null }): string | null {
  return holdsOfferWording(p.name ?? "") ?? holdsOfferWording(p.description ?? "") ?? (p.url ? holdsOfferWording(urlWords(p.url)) : null);
}

/**
 * May this product be put in front of a customer now. Order is the order a
 * person would say it in: sold, unavailable, unknown, then the record's age,
 * then the price. The first failure is the one returned, so the button's
 * disabled reason is stable. A non-integer or non-positive price reads as no
 * price: a figure that cannot be an exact count of cents is not a fact.
 */
export function catalogInsertable(
  p: Pick<CatalogProduct, "priceCents" | "availability" | "fetchedAt" | "ttlSeconds" | "name" | "url"> & { readonly description?: string | null },
  now: Date,
): CatalogInsertable {
  if (p.availability === "sold") return { ok: false, reason: "sold" };
  if (p.availability === "unavailable") return { ok: false, reason: "unavailable" };
  if (p.availability === "unknown") return { ok: false, reason: "unknown_availability" };
  if (now.getTime() > p.fetchedAt.getTime() + p.ttlSeconds * 1000) return { ok: false, reason: "stale" };
  if (p.priceCents === null || !Number.isSafeInteger(p.priceCents) || p.priceCents <= 0) return { ok: false, reason: "no_price" };
  if (productOfferWording(p) !== null) return { ok: false, reason: "offer_wording" };
  return { ok: true };
}

/** The words the rail shows beside a disabled Insert. Plain, no offer, no blame. */
export const CATALOG_REFUSAL_WORDS: Readonly<Record<CatalogRefusal, string>> = Object.freeze({
  sold: "Sold, so it cannot be offered.",
  unavailable: "Marked unavailable.",
  unknown_availability: "Availability is not known.",
  stale: "The price is out of date. Confirm it first.",
  no_price: "No price on record.",
  offer_wording: "The name or link carries offer wording. Fix it in the catalog before it can be inserted.",
});

/** The reference a draft carries for an insert: the product and the version whose price was read. */
export function productFactRef(p: Pick<CatalogProduct, "id" | "version">): string {
  return `product:${p.id}@${p.version}`;
}

/**
 * The one text a product becomes in a draft: `<name>, <price>` then the link on
 * its own line. Registered as the template `catalog.product_insert` in
 * `outboundText.ts`. Throws on a product with no integer price: the caller was
 * to check `catalogInsertable` first, and a throw is louder than a line that
 * reads as a price and is not one.
 */
export function composeProductInsert(
  p: Pick<CatalogProduct, "id" | "version" | "name" | "priceCents" | "url"> & {
    readonly description?: string | null;
    /**
     * G48 (IC 2.2, "Insert action puts photo + name + price + link"): the `media_assets` id of the
     * piece's photo (`product_media`, lowest position), read by the caller. It adds no text: it is
     * returned as is, for the insert row and the cards to carry. Sending the image is a vendor step
     * that is not built (nothing in the send path reads it).
     */
    readonly photoAssetId?: string | null;
  },
): { readonly text: string; readonly factRef: string; readonly photoAssetId?: string } {
  if (p.priceCents === null || !Number.isSafeInteger(p.priceCents) || p.priceCents <= 0) {
    throw new Error(`composeProductInsert: product ${p.id} has no usable price in integer cents; an insert is never composed without one`);
  }
  const name = p.name.trim();
  if (name === "") throw new Error(`composeProductInsert: product ${p.id} has a blank name`);
  if (productOfferWording(p) !== null) {
    throw new Error(`composeProductInsert: product ${p.id} carries offer wording in its name or link; no automated message states an offer (section 0.5)`);
  }
  const head = `${name}, ${formatCents(p.priceCents)}`;
  const url = p.url?.trim() ?? "";
  const composed = { text: url === "" ? head : `${head}\n${url}`, factRef: productFactRef(p) };
  if (p.photoAssetId === undefined || p.photoAssetId === null) return composed;
  if (!PHOTO_ASSET_ID.test(p.photoAssetId)) {
    throw new Error(`composeProductInsert: product ${p.id} names a photo id that is not a media asset id; a photo is carried only by its stored id`);
  }
  return { ...composed, photoAssetId: p.photoAssetId };
}

/** G48: the shape of a `media_assets` id a product insert may carry (the rail's insert ref id shape). */
const PHOTO_ASSET_ID = /^[A-Za-z0-9_.:-]{1,128}$/;

/** What a media asset's rule reads. */
export interface CatalogMedia {
  readonly source: MediaSource;
  readonly rights: MediaRights;
  /** The contact who shared it. Set exactly when `source` is `customer_shared`. */
  readonly contactId: string | null;
  readonly fetchedAt?: Date;
  readonly ttlSeconds?: number;
}

export type MediaRefusal = "other_contacts_media" | "rights_mismatch" | "stale";
export type MediaInsertable = { readonly ok: true } | { readonly ok: false; readonly reason: MediaRefusal };

/**
 * May this asset go into the thread of `threadContactId`. A customer's own
 * photo is insertable only into the thread of the contact who shared it, unless
 * the owner recorded `permission_granted` (the one rights value that lifts the
 * limit). The shop's own media (`owned`, `licensed`) goes anywhere. Rights that
 * do not fit the source (`customer_own` on a shop post) is a data fault and is
 * refused, never read as owned. Staleness is checked only when `now` is given,
 * so a caller that has no clock does not get a silent pass on freshness it did
 * not ask about: it passes `now` or accepts that.
 */
export function mediaInsertable(asset: CatalogMedia, threadContactId: string, now?: Date): MediaInsertable {
  const shared = asset.source === "customer_shared";
  if (shared !== (asset.contactId !== null)) return { ok: false, reason: "rights_mismatch" };
  if (asset.rights === "customer_own" && !shared) return { ok: false, reason: "rights_mismatch" };
  if (shared && asset.rights !== "customer_own" && asset.rights !== "permission_granted") return { ok: false, reason: "rights_mismatch" };
  if (shared && asset.rights !== "permission_granted" && asset.contactId !== threadContactId) {
    return { ok: false, reason: "other_contacts_media" };
  }
  if (now !== undefined && asset.fetchedAt !== undefined && asset.ttlSeconds !== undefined) {
    if (now.getTime() > asset.fetchedAt.getTime() + asset.ttlSeconds * 1000) return { ok: false, reason: "stale" };
  }
  return { ok: true };
}

/**
 * Keep the tags at or above the confidence floor, one per (kind, value), the
 * most confident. A tag with a kind this module does not know, a blank value or
 * a confidence outside 0..1 is dropped here and counted by the caller as
 * refused; it is never stored. The model never grades a fact (section 4): a
 * tag is an inference, stored as one, and no price or availability reads it.
 */
export function keepVisionTags(tags: readonly VisionTag[], min: number = VISION_TAG_MIN_CONFIDENCE): VisionTag[] {
  const best = new Map<string, VisionTag>();
  for (const t of tags) {
    if (!(VISION_TAG_KINDS as readonly string[]).includes(t.kind)) continue;
    const value = t.value.trim().toLowerCase();
    if (value === "" || value.length > 64) continue;
    if (!Number.isFinite(t.confidence) || t.confidence < min || t.confidence > 1) continue;
    const key = `${t.kind}\u0000${value}`;
    const held = best.get(key);
    if (held === undefined || held.confidence < t.confidence) best.set(key, { kind: t.kind, value, confidence: t.confidence });
  }
  return [...best.values()].sort((a, b) => a.kind.localeCompare(b.kind) || a.value.localeCompare(b.value));
}

/** The spec keys a product row keeps, in the order a card prints them. */
export const SPEC_KEYS = ["metal", "stone", "cut", "setting", "style"] as const;
export type SpecKey = (typeof SPEC_KEYS)[number];

/** A product as read from a CSV row or a feed record, before it has an id or a clock. */
export interface ProductDraftRow {
  readonly sourceRef: string;
  readonly sku: string | null;
  readonly name: string;
  readonly priceCents: number | null;
  readonly currency: string;
  readonly availability: ProductAvailability;
  readonly url: string | null;
  readonly specs: Readonly<Partial<Record<SpecKey, string>>>;
}

/**
 * The hash of what a product row SAYS, so an upsert bumps `version` only when
 * a field a customer could see changed. Canonical: fixed key order, no clock.
 */
export function productContentHash(r: ProductDraftRow): string {
  const canonical = JSON.stringify([
    r.sku,
    r.name,
    r.priceCents,
    r.currency,
    r.availability,
    r.url,
    SPEC_KEYS.map((k) => r.specs[k] ?? null),
  ]);
  return createHash("sha256").update(canonical).digest("hex");
}

export type CsvRefusalReason =
  | "unknown_column"
  | "missing_column"
  | "unterminated_quote"
  | "wrong_field_count"
  | "missing_value"
  | "unreadable_price"
  | "price_not_positive"
  | "bad_availability"
  | "bad_url"
  | "too_long"
  | "duplicate_source_ref"
  | "duplicate_sku"
  | "sku_taken";

export interface CsvRefusal {
  /** 1 is the header record, 2 the first data record. Records, not physical lines. */
  readonly row: number;
  /** The column header it names, or "" for a whole-record refusal. */
  readonly column: string;
  readonly reason: CsvRefusalReason;
  /** For a repeated reference or SKU: the earlier row it repeats. */
  readonly otherRow?: number;
}

export interface ParsedCatalogCsv {
  readonly rows: readonly (ProductDraftRow & { readonly row: number })[];
  readonly refusals: readonly CsvRefusal[];
}

/** The columns a catalog CSV may carry. Anything else refuses the file (never a silent drop). */
export const CATALOG_CSV_COLUMNS = ["source_ref", "sku", "name", "price", "availability", "url", ...SPEC_KEYS] as const;

const NAME_MAX = 200; // AWAITING RATIFICATION
const FIELD_MAX = 500; // AWAITING RATIFICATION

/** RFC 4180 records. Null `records` with an unterminated quote at record `row`. */
function readCsvRecords(text: string): { records: string[][]; unterminatedAt: number | null } {
  const src = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const records: string[][] = [];
  let field = "";
  let record: string[] = [];
  let inQuotes = false;
  let quotedField = false;
  let i = 0;
  const endField = (): void => {
    record.push(field);
    field = "";
    quotedField = false;
  };
  const endRecord = (): void => {
    endField();
    // A wholly blank physical line is not a record.
    if (!(record.length === 1 && record[0] === "")) records.push(record);
    record = [];
  };
  while (i < src.length) {
    const c = src[i] as string;
    if (inQuotes) {
      if (c === '"') {
        if (src[i + 1] === '"') {
          field += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
        i += 1;
        continue;
      }
      field += c;
      i += 1;
      continue;
    }
    if (c === '"' && field === "" && !quotedField) {
      inQuotes = true;
      quotedField = true;
      i += 1;
      continue;
    }
    if (c === ",") {
      endField();
      i += 1;
      continue;
    }
    if (c === "\r" || c === "\n") {
      if (c === "\r" && src[i + 1] === "\n") i += 1;
      endRecord();
      i += 1;
      continue;
    }
    field += c;
    i += 1;
  }
  if (inQuotes) return { records, unterminatedAt: records.length + 1 };
  if (field !== "" || record.length > 0 || quotedField) endRecord();
  return { records, unterminatedAt: null };
}

/**
 * A catalog CSV, RFC 4180, header required. Every refusal names its record and
 * column. A refused row is left out and the rest are returned: the owner sees
 * exactly which rows were refused and commits the others (the design's e2e).
 * A file whose header the parser cannot trust (an unknown column, or no `name`,
 * or neither `source_ref` nor `sku`) refuses with NO rows: a misread header
 * would put one column's values into another's field, and that is a price in
 * the wrong place.
 *
 * `price` reads through `readTypedDollars`, the one money reader (F-16): the
 * whole cell must be one exact amount ("1250", "$1,250.00"), "1250 or so" and
 * "$5.5" refuse. A blank price is allowed and stored as no price (the product
 * then shows as not insertable). Currency is USD in v1: a catalog in another
 * currency is a spec the owner has not given (FINDINGS TA-X2-03).
 */
export function parseCatalogCsv(text: string): ParsedCatalogCsv {
  const refusals: CsvRefusal[] = [];
  const { records, unterminatedAt } = readCsvRecords(text);
  if (unterminatedAt !== null) {
    return { rows: [], refusals: [{ row: unterminatedAt, column: "", reason: "unterminated_quote" }] };
  }
  const header = records[0];
  if (header === undefined) return { rows: [], refusals: [{ row: 1, column: "", reason: "missing_column" }] };
  const cols = header.map((h) => h.trim().toLowerCase());
  for (const c of cols) {
    if (!(CATALOG_CSV_COLUMNS as readonly string[]).includes(c)) refusals.push({ row: 1, column: c, reason: "unknown_column" });
  }
  if (!cols.includes("name")) refusals.push({ row: 1, column: "name", reason: "missing_column" });
  if (!cols.includes("source_ref") && !cols.includes("sku")) refusals.push({ row: 1, column: "source_ref", reason: "missing_column" });
  if (new Set(cols).size !== cols.length) {
    const dup = cols.find((c, i) => cols.indexOf(c) !== i) ?? "";
    refusals.push({ row: 1, column: dup, reason: "unknown_column" });
  }
  if (refusals.length > 0) return { rows: [], refusals };

  const rows: (ProductDraftRow & { row: number })[] = [];
  const seen = new Set<string>();
  const seenSku = new Map<string, number>();
  records.slice(1).forEach((rec, idx) => {
    const row = idx + 2;
    if (rec.length !== cols.length) {
      refusals.push({ row, column: "", reason: "wrong_field_count" });
      return;
    }
    const cell = (c: string): string => (rec[cols.indexOf(c)] ?? "").trim();
    const has = (c: string): boolean => cols.includes(c);
    const before = refusals.length;
    const sku = has("sku") ? cell("sku") : "";
    const sourceRef = cell("source_ref") !== "" ? cell("source_ref") : sku;
    const name = has("name") ? cell("name") : "";
    if (sourceRef === "") refusals.push({ row, column: has("source_ref") ? "source_ref" : "sku", reason: "missing_value" });
    if (name === "") refusals.push({ row, column: "name", reason: "missing_value" });
    for (const c of cols) {
      if (cell(c).length > (c === "name" ? NAME_MAX : FIELD_MAX)) refusals.push({ row, column: c, reason: "too_long" });
    }

    let priceCents: number | null = null;
    const rawPrice = has("price") ? cell("price") : "";
    if (rawPrice !== "") {
      const read = readTypedDollars(rawPrice);
      if (!read.ok) refusals.push({ row, column: "price", reason: "unreadable_price" });
      else if (read.cents <= 0) refusals.push({ row, column: "price", reason: "price_not_positive" });
      else priceCents = read.cents;
    }

    let availability: ProductAvailability = "unknown";
    const rawAvail = has("availability") ? cell("availability").toLowerCase().replace(/[\s-]+/g, "_") : "";
    if (rawAvail !== "") {
      if ((PRODUCT_AVAILABILITIES as readonly string[]).includes(rawAvail)) availability = rawAvail as ProductAvailability;
      else refusals.push({ row, column: "availability", reason: "bad_availability" });
    }

    const rawUrl = has("url") ? cell("url") : "";
    if (rawUrl !== "" && !/^https:\/\/[^\s]+$/i.test(rawUrl)) refusals.push({ row, column: "url", reason: "bad_url" });

    if (sourceRef !== "" && seen.has(sourceRef)) refusals.push({ row, column: has("source_ref") ? "source_ref" : "sku", reason: "duplicate_source_ref" });
    const skuKey = sku.trim().toUpperCase();
    if (skuKey !== "" && !(refusals.length > before)) {
      const first = seenSku.get(skuKey);
      if (first !== undefined) refusals.push({ row, column: "sku", reason: "duplicate_sku", otherRow: first });
    }
    if (refusals.length > before) return;
    seen.add(sourceRef);
    if (skuKey !== "") seenSku.set(skuKey, row);

    const specs: Partial<Record<SpecKey, string>> = {};
    for (const k of SPEC_KEYS) {
      if (has(k) && cell(k) !== "") specs[k] = cell(k);
    }
    rows.push({
      row,
      sourceRef,
      sku: sku === "" ? null : sku,
      name,
      priceCents,
      currency: "USD",
      availability,
      url: rawUrl === "" ? null : rawUrl,
      specs,
    });
  });
  return { rows, refusals };
}

/** The words for a CSV refusal, one line, naming row and column. Never echoes the cell. */
export function csvRefusalLine(r: CsvRefusal): string {
  const where = r.column === "" ? `Row ${r.row}` : `Row ${r.row}, column ${r.column}`;
  const why: Record<CsvRefusalReason, string> = {
    unknown_column: "is not a column this import reads",
    missing_column: "is required and missing from the header",
    unterminated_quote: "has a quote that never closes, so the file cannot be read past it",
    wrong_field_count: "has a different number of fields from the header",
    missing_value: "is blank and cannot be",
    unreadable_price: "is not one exact dollar amount",
    price_not_positive: "must be more than zero",
    bad_availability: "is not in_stock, low, sold, unavailable or unknown",
    bad_url: "is not an https link",
    too_long: "is too long",
    duplicate_source_ref: "repeats an earlier row's reference",
    duplicate_sku: `repeats the SKU of row ${r.otherRow ?? "?"}`,
    sku_taken: "is already used by another product in the catalog",
  };
  return `${where} ${why[r.reason]}.`;
}
